import * as React from 'react'
import { useOutletContext, useParams } from 'react-router-dom'

import { Box, RadioButton, Text, Touchable } from '@factorialco/deprecated-design-system'
import {
  ApprovalGroupDetailsFragment,
  ApprovalsValueObjectsRulesConfigurationRuleDecisionEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { ApprovalFlowForm } from 'modules/approvals/components/ApprovalFlowForm'
import { AddStepButton } from 'modules/approvals/components/ApprovalFlowForm/AddStepButton'
import type { ApprovalFlowArgs } from 'modules/approvals/lib/approvalFlows'

import { IfRulesNotMet } from './IfRulesNotMet'
import { cardStyle, formStyle } from './onRulesMet.vanilla.css'

interface Props {
  ruleName?: string | undefined
  flow: ApprovalFlowArgs | null
  onRulesMetValue: ApprovalsValueObjectsRulesConfigurationRuleDecisionEnum | null
  onChange: (
    approvalFlow: ApprovalFlowArgs | null,
    onRulesMet: ApprovalsValueObjectsRulesConfigurationRuleDecisionEnum
  ) => void
}

export const OnRulesMet = ({ ruleName, flow, onRulesMetValue, onChange }: Props) => {
  const [selected, setSelected] = React.useState(onRulesMetValue ?? 'auto_approve')
  const [showAddModal, setShowAddModal] = React.useState(false)

  const [flowData, setFlowData] = React.useState(flow)
  const { group } = useOutletContext<{ group: ApprovalGroupDetailsFragment }>()

  const params = useParams()
  const { rules_configuration_id } = params

  const onRulesMeetChanged = (
    onRulesMet: ApprovalsValueObjectsRulesConfigurationRuleDecisionEnum
  ) => {
    setSelected(onRulesMet)
    onChange(flowData, onRulesMet)
  }

  const onFlowChanged = (approvalFlow: ApprovalFlowArgs | null) => {
    setFlowData(approvalFlow)
    onChange(approvalFlow, selected)
  }

  const decisionOptions = [
    {
      value: 'auto_approve',
      label: i18n.t('settings.approvals.approval_rule.decisions.auto_approve'),
    },
    {
      value: 'custom_approval_flow',
      label: i18n.t('settings.approvals.approval_rule.decisions.custom_approval_flow'),
    },
  ] as const

  const cardOption = (
    value: ApprovalsValueObjectsRulesConfigurationRuleDecisionEnum,
    label: string
  ) => {
    return (
      <Box
        borderRadius={{ all: 'abs008' }}
        border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
        padding="s16"
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        className={cardStyle}
      >
        <Touchable onClick={() => onRulesMeetChanged(value)}>
          <Text size="300" weight="normal" color="grey1000">
            {label}
          </Text>
        </Touchable>
        <RadioButton
          value={value}
          checked={selected === value}
          onChange={() => onRulesMeetChanged(value)}
          size="medium"
        />
      </Box>
    )
  }

  return (
    <>
      <Box paddingTop="s16" flexDirection="row" alignItems="center" gap="s8">
        <Text.Normal size="200" weight="normal" color="grey800">
          {i18n.t('settings.approvals.approval_rule.configuration.if_met')}
        </Text.Normal>
      </Box>
      <Box paddingY="s8" gap="s8">
        {decisionOptions.map(({ label, value }) => {
          return cardOption(value, label)
        })}
        {selected === 'custom_approval_flow' && (
          <>
            <Box paddingTop="s8" className={formStyle}>
              <ApprovalFlowForm
                approvalFlow={flow}
                showAddModal={showAddModal}
                canEdit
                canAddSteps={false}
                canDeleteSteps
                onChange={(updatedFlow) => onFlowChanged(updatedFlow)}
                onCloseModal={() => setShowAddModal(false)}
              />
            </Box>
            <AddStepButton addStep={() => setShowAddModal(true)} />
          </>
        )}
      </Box>
      {group.domainConfigurationRules.length > 1 && (
        <IfRulesNotMet
          group={group}
          rulesConfigurationId={rules_configuration_id}
          ruleName={ruleName}
        />
      )}
    </>
  )
}
